import { useEffect, useRef, useState } from "react";
import type { SavedViewDto } from "@gw/shared-types";

interface Props {
  views: SavedViewDto[];
  onApply: (view: SavedViewDto) => void;
  onSaveClick: () => void;
  onDelete: (view: SavedViewDto) => void;
  unavailable?: boolean;
}

export function SavedViewsMenu({ views, onApply, onSaveClick, onDelete, unavailable }: Props) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={menuRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={unavailable ? "Saved views require database setup" : undefined}
      >
        <span className="material-symbols-rounded">bookmark</span>
        Saved views
      </button>
      {open && (
        <div
          role="menu"
          className="dropdown-menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            background: "white",
            border: "1px solid var(--border-subtle)",
            borderRadius: 6,
            padding: 4,
            minWidth: 240,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            zIndex: 50,
          }}
        >
          {unavailable ? (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--fg-3)" }}>
              Saved views require database setup.
            </div>
          ) : views.length === 0 ? (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--fg-3)" }}>
              No saved views yet.
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {views.map((v) => (
                <div
                  key={v.id}
                  role="menuitem"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gw-neutral-50)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={() => {
                    onApply(v);
                    setOpen(false);
                  }}
                >
                  <span style={{ flex: 1, fontSize: 13 }}>{v.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(v);
                    }}
                    aria-label={`Delete saved view ${v.name}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--fg-3)",
                      padding: 2,
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
                      delete
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ borderTop: "1px solid var(--border-subtle)", marginTop: 4 }}>
            <button
              type="button"
              disabled={unavailable}
              onClick={() => {
                setOpen(false);
                onSaveClick();
              }}
              style={{
                width: "100%",
                background: "none",
                border: "none",
                textAlign: "left",
                padding: "8px 10px",
                fontSize: 13,
                cursor: unavailable ? "not-allowed" : "pointer",
                color: unavailable ? "var(--fg-3)" : "var(--gw-primary-500)",
                fontWeight: 600,
              }}
            >
              + Save current view…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
